import { Link } from "react-router-dom";
import { Home, Search, Bell, User as UserIcon, Bookmark, Film, PlaySquare, Layers } from "lucide-react";
import { useTabHistory } from "@/shared/state/use-tab-history";
import { ACCENTS } from "@/shared/theme";
import { type Mode } from "@/shared/mode/types";

type Slot = { href: string; label: string; icon: React.ReactNode };

function buildSlots(mode: Mode): { left: [Slot, Slot]; right: [Slot, Slot] } {
  if (mode === "movieflix") {
    return {
      left: [
        { href: "/movieflix", label: "Movies", icon: <Film className="w-5 h-5" /> },
        { href: "/movieflix/search", label: "Search", icon: <Search className="w-5 h-5" /> },
      ],
      right: [
        { href: "/movieflix/my-list", label: "My List", icon: <Bookmark className="w-5 h-5" /> },
        { href: "/instaflix/profile/you", label: "Profile", icon: <UserIcon className="w-5 h-5" /> },
      ],
    };
  }
  if (mode === "reelsflix") {
    return {
      left: [
        { href: "/reelsflix", label: "Reels", icon: <PlaySquare className="w-5 h-5" /> },
        { href: "/reelsflix/search", label: "Search", icon: <Search className="w-5 h-5" /> },
      ],
      right: [
        { href: "/instaflix/notifications", label: "Notifications", icon: <Bell className="w-5 h-5" /> },
        { href: "/instaflix/profile/you", label: "Profile", icon: <UserIcon className="w-5 h-5" /> },
      ],
    };
  }
  return {
    left: [
      { href: "/instaflix", label: "Home", icon: <Home className="w-5 h-5" /> },
      { href: "/instaflix/search", label: "Search", icon: <Search className="w-5 h-5" /> },
    ],
    right: [
      { href: "/instaflix/notifications", label: "Notifications", icon: <Bell className="w-5 h-5" /> },
      { href: "/instaflix/profile/you", label: "Profile", icon: <UserIcon className="w-5 h-5" /> },
    ],
  };
}

export function BottomNav({ onOpenDrawer }: { onOpenDrawer: () => void }) {
  const { currentMode, tapMode, MODES } = useTabHistory();
  const accent = ACCENTS[currentMode];
  const { left, right } = buildSlots(currentMode);

  return (
    <nav
      className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md z-40 pointer-events-none"
      data-testid="bottomnav"
      data-active-mode={currentMode}
    >
      {/* Per-mode tap targets for e2e/automation. Off-screen but clickable. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: 1,
          height: 1,
          overflow: 'hidden',
          pointerEvents: 'auto',
        }}
      >
        {MODES.map((m) => (
          <button
            key={m}
            type="button"
            data-testid={`bottomnav-${m}`}
            onClick={() => tapMode(m)}
            tabIndex={-1}
          >
            Switch to {m}
          </button>
        ))}
      </div>
      <div className="glass-strong border-t border-white/[0.08] px-6 py-4 pb-7 flex items-center justify-between pointer-events-auto">
        {left.map((s) => (
          <Link key={s.href} to={s.href} aria-label={s.label} className="p-2 -m-2 text-white/60 hover:text-white active:scale-90 transition-all">
            {s.icon}
          </Link>
        ))}
        <button
          onClick={onOpenDrawer}
          aria-label="Switch mode"
          className="-mt-7 w-14 h-14 rounded-full p-[1.5px] active:scale-95 transition-transform"
          style={{ background: `linear-gradient(135deg, ${accent.primary}, transparent)`, boxShadow: `0 8px 20px ${accent.glow}` }}
        >
          <div className="w-full h-full rounded-full bg-velvet flex items-center justify-center">
            <Layers className="w-5 h-5" style={{ color: accent.primary }} />
          </div>
        </button>
        {right.map((s) => (
          <Link key={s.href} to={s.href} aria-label={s.label} className="p-2 -m-2 text-white/60 hover:text-white active:scale-90 transition-all">
            {s.icon}
          </Link>
        ))}
      </div>
    </nav>
  );
}
